import { AxiosError } from "axios";
import { InfoBox } from "components/InfoBox/InfoBox";
import { CREDIT_LIMIT_DECREASE_HATEOAS_KEY } from "components/ReduceCreditLimit/constants";
import { nzProductCodes } from "constants/products";
import { Account } from "library/accounts/types";
import { useProfile } from "library/hooks/auth/useProfile";
import { Product } from "library/hooks/types";
import { useConfigFeature } from "library/hooks/useConfig";
import { FC } from "react";
import { useEventTracking } from "react-event-tracker";
import { useQuery } from "react-query";
import { Link } from "react-router-dom";
import { AccordionLineItem } from "./AccordionLineItem";
import { additionalCardHolderMap, legalMap } from "./assetMap";
import {
  getRequestStatus,
  RequestStatusType,
  REQUEST_STATUSES,
} from "./getRequestStatus";
import styles from "./manage-account-content.module.scss";

const getItemStatus = (
  status: RequestStatusType,
): RequestStatusType | undefined =>
  [
    REQUEST_STATUSES.SUBMITTED,
    REQUEST_STATUSES.IN_PROGRESS,
    REQUEST_STATUSES.COMPLETED,
  ].includes(status)
    ? status
    : undefined;

interface Props {
  account: Account;
  product?: Product;
}

export const CardAccordionContent: FC<Props> = ({ account, product }) => {
  const { trackEvent } = useEventTracking();
  const { profile } = useProfile();
  const userEmail = profile?.email ?? "";
  const accountNumber = account.account_number as string;
  const token = account.credit_card?.token ?? "";

  const closeAccountInfo = useQuery<RequestStatusType, AxiosError>(
    ["closeAccountStatus", { userEmail, accountNumber }],
    () =>
      getRequestStatus(
        {
          userEmail,
          accountNumber,
          requestType: "closeAccount",
        },
        token,
      ),
  );
  const thirdPartyAuthorityInfo = useQuery<RequestStatusType, AxiosError>(
    ["thirdPartyAuthorityStatus", { userEmail, accountNumber }],
    () =>
      getRequestStatus(
        {
          userEmail,
          accountNumber,
          requestType: "thirdPartyAuthority",
        },
        token,
      ),
  );

  const increaseCreditLimitNzInfo = useQuery<RequestStatusType, AxiosError>(
    ["increaseCreditLimitNzStatus", { userEmail, accountNumber }],
    () =>
      getRequestStatus(
        {
          userEmail,
          accountNumber,
          requestType: "increaseCreditLimitNz",
        },
        token,
      ),
  );

  const { links } = account;
  const showActivate =
    useConfigFeature("cardActivation") &&
    links.some(({ rel }) => rel === "card_activation");
  const showOverseasTravel = useConfigFeature("overseasTravel");

  const showStatements =
    useConfigFeature("eStatements") &&
    links.some(({ rel }) => rel === "statements");
  const showPin =
    useConfigFeature("setPin") && links.some(({ rel }) => rel === "pin_change");
  const isIncreaseCreditLimitAUAvailable = useConfigFeature(
    "increaseCreditLimitAU",
  );
  const isIncreaseCreditLimitNZAvailable = useConfigFeature(
    "increaseCreditLimitNZ",
  );
  const showStatementPreferences = useConfigFeature("showStatementPreferences");
  const showIncreaseCreditLimit =
    product?.region === "AU"
      ? isIncreaseCreditLimitAUAvailable
      : product?.region === "NZ"
      ? isIncreaseCreditLimitNZAvailable
      : false;
  const showRequestLetter = useConfigFeature("requestLetter");
  const isAdditionalCardHolderAvailable = useConfigFeature(
    "additionalCardHolder",
  );
  const isThirdPartyAuthorityAvailable = useConfigFeature(
    "thirdPartyAuthority",
  );
  const isAu = product?.region === "AU";

  const isTransactionDisputeAvailable = useConfigFeature("disputeTransaction");
  const showTempCardLock = useConfigFeature("tempCardLockUnlock");
  const showLostStolenOrDamaged = useConfigFeature("lostStolenOrDamaged");

  const legalCondition = product?.name && legalMap[product?.name];
  const additionalCardHolderCondition =
    product?.name && additionalCardHolderMap[product?.name];
  const showRCL = links.some(
    ({ rel }) => rel === CREDIT_LIMIT_DECREASE_HATEOAS_KEY,
  );

  if (!showPin && !showStatements && !showActivate && !isAu) {
    return (
      <div className={styles.accordionContent}>
        <InfoBox
          grey
          iconName="customer-service-rep"
          className={styles.noContent}
        >
          <h3>
            Hi, at this time the ability to manage your account isn't available.
          </h3>
          <p>
            We're working on adding these services soon and we will inform you
            when they become available.
          </p>
        </InfoBox>
      </div>
    );
  }

  const isAccountClosed = account.status === "CLOSED";
  const hasCloseAccountAvailableLink =
    closeAccountInfo.isError ||
    closeAccountInfo.data === REQUEST_STATUSES.NOT_REQUESTED;

  const hasThirdPartyAuthorityInfoAvailableLink =
    thirdPartyAuthorityInfo.isError ||
    thirdPartyAuthorityInfo.data === REQUEST_STATUSES.NOT_REQUESTED;

  const hasIncreaseCreditLimitNZLinkAvailable =
    increaseCreditLimitNzInfo.isError ||
    increaseCreditLimitNzInfo.data === REQUEST_STATUSES.NOT_REQUESTED;

  const showIncreaseCreditLimitForNzProduct = nzProductCodes.includes(
    product?.code ?? "",
  );
  // If there is credit_limit_increase link in account, credit limit increase is eligible
  const isCliEligible = account?.links.find(
    (link) => link.rel === "credit_limit_increase",
  )?.href;
  return (
    <div className={styles.accordionContent}>
      <h3 className="h6">Account</h3>
      {showStatements && (
        <AccordionLineItem
          icon="icon-view-statements"
          to="/credit-card-account"
          state={{
            activeTab: "statements",
            account,
            product,
          }}
          onClick={() => {
            trackEvent({
              event: {
                category: "accordion",
                action: "in-page-navigation",
                location: "manage-my-account",
                label: "View / download statements",
              },
            });
          }}
        >
          View / download statements
        </AccordionLineItem>
      )}
      {isCliEligible &&
        showIncreaseCreditLimit &&
        showIncreaseCreditLimitForNzProduct && (
          <AccordionLineItem
            component={Link}
            icon="increase-credit-limit"
            to="/increase-credit-limit"
            data-testid="showIncreaseCreditLimitForNzProducts"
            state={{ account, product }}
            status={
              increaseCreditLimitNzInfo.data &&
              getItemStatus(increaseCreditLimitNzInfo.data)
            }
            onClick={() => {
              trackEvent({
                event: {
                  category: "accordion",
                  action: "in-page-navigation",
                  location: "manage-my-account",
                  label: "Increase credit limit",
                },
              });
            }}
          >
            Increase credit limit
          </AccordionLineItem>
        )}
      {showRCL && (
        <AccordionLineItem
          component={Link}
          icon="icon-reduce-credit"
          to="/reduce-credit-limit"
          state={{ account }}
          onClick={() => {
            trackEvent({
              event: {
                category: "accordion",
                action: "in-page-navigation",
                location: "manage-my-account",
                label: "Reduce credit limit",
              },
            });
          }}
        >
          Reduce credit limit
        </AccordionLineItem>
      )}

      {legalCondition !== "" && (
        <AccordionLineItem
          component="a"
          target="_blank"
          rel="noopener noreferrer"
          to={`https://assets.latitudefinancial.com/legals/conditions-of-use/${legalCondition}/cou.pdf`}
          icon="close-account"
          onClick={() => {
            trackEvent({
              event: {
                category: "accordion",
                action: "in-page-navigation",
                location: "manage-my-account",
                label: "Conditions of Use",
              },
            });
          }}
        >
          Conditions of use
        </AccordionLineItem>
      )}

      {showIncreaseCreditLimit && !showIncreaseCreditLimitForNzProduct && (
        <AccordionLineItem
          component={hasIncreaseCreditLimitNZLinkAvailable ? Link : "div"}
          icon="increase-credit-limit"
          to={
            hasIncreaseCreditLimitNZLinkAvailable
              ? "/increase-credit-limit"
              : ""
          }
          data-testid="showIncreaseCreditLimit"
          state={{ account, product }}
          status={
            increaseCreditLimitNzInfo.data &&
            getItemStatus(increaseCreditLimitNzInfo.data)
          }
          onClick={() => {
            trackEvent({
              event: {
                category: "accordion",
                action: "in-page-navigation",
                location: "manage-my-account",
                label: "Increase credit limit",
              },
            });
          }}
        >
          Increase credit limit
        </AccordionLineItem>
      )}
      {isAdditionalCardHolderAvailable ? (
        <AccordionLineItem
          component={Link}
          icon="additional-card-holder"
          to="/additional-card-holder"
          state={{ account }}
          onClick={() => {
            trackEvent({
              event: {
                category: "accordion",
                action: "open-accordion",
                location: "additional-card-holder",
                label: "Additional card holder",
              },
            });
          }}
        >
          Additional card holder
        </AccordionLineItem>
      ) : additionalCardHolderCondition !== "" ? (
        <AccordionLineItem
          component="a"
          target="_blank"
          rel="noopener noreferrer"
          to={additionalCardHolderCondition}
          icon="additional-card-holder"
          onClick={() => {
            trackEvent({
              event: {
                category: "accordion",
                action: "in-page-navigation",
                location: "manage-my-account",
                label: "Additional card holder",
              },
            });
          }}
        >
          Additional card holder
        </AccordionLineItem>
      ) : null}

      {isAu && isThirdPartyAuthorityAvailable && (
        <AccordionLineItem
          component={hasThirdPartyAuthorityInfoAvailableLink ? Link : "div"}
          icon="third-party-authority"
          to={
            hasThirdPartyAuthorityInfoAvailableLink
              ? "/third-party-authority"
              : ""
          }
          state={{ account }}
          status={
            thirdPartyAuthorityInfo.data &&
            getItemStatus(thirdPartyAuthorityInfo.data)
          }
          onClick={() => {
            trackEvent({
              event: {
                category: "accordion",
                action: "open-accordion",
                location: "third-party-authority",
                label: "Third party authority",
              },
            });
          }}
        >
          Third party authority
        </AccordionLineItem>
      )}

      {showRequestLetter && (
        <AccordionLineItem
          component={Link}
          icon="mail-closed"
          to="/request-letter"
          state={{ account }}
          onClick={() => {
            trackEvent({
              event: {
                category: "accordion",
                action: "in-page-navigation",
                location: "manage-my-account",
                label: "Request a letter",
              },
            });
          }}
        >
          Request a letter
        </AccordionLineItem>
      )}
      {isTransactionDisputeAvailable && (
        <AccordionLineItem
          component={Link}
          icon="credit-card-attention"
          to="/transaction-dispute"
          state={{ account }}
          onClick={() => {
            trackEvent({
              event: {
                category: "accordion",
                action: "open-accordion",
                location: "manage-my-account",
                label: "Transaction dispute",
              },
            });
          }}
        >
          Transaction dispute
        </AccordionLineItem>
      )}

      {showOverseasTravel && (
        <AccordionLineItem
          component={Link}
          icon="plane"
          to="/overseas-travel"
          state={{ account }}
          onClick={() => {
            trackEvent({
              event: {
                category: "accordion",
                action: "open-accordion",
                location: "manage-my-account",
                label: "Notify us of when you're travelling overseas",
              },
            });
          }}
        >
          Notify us of when you're travelling overseas
        </AccordionLineItem>
      )}

      {!isAccountClosed ? (
        <AccordionLineItem
          component={hasCloseAccountAvailableLink ? Link : "div"}
          icon="close-account"
          to={hasCloseAccountAvailableLink ? "/close-account" : ""}
          status={closeAccountInfo.data && getItemStatus(closeAccountInfo.data)}
          state={{ account }}
          onClick={() => {
            trackEvent({
              event: {
                category: "accordion",
                action: "in-page-navigation",
                location: "manage-my-account",
                label: "Close account request",
              },
            });
          }}
        >
          Close account request
        </AccordionLineItem>
      ) : null}
      {showStatementPreferences && (
        <AccordionLineItem
          icon="statement-preference"
          to="/statement-preferences"
          state={{ account }}
          onClick={() => {
            trackEvent({
              event: {
                category: "accordion",
                action: "in-page-navigation",
                location: "manage-my-account",
                label: "Statement preferences",
              },
            });
          }}
        >
          Statement preferences
        </AccordionLineItem>
      )}

      {(showActivate ||
        showPin ||
        showTempCardLock ||
        showLostStolenOrDamaged) && <h3 className="h6">Card</h3>}
      {showActivate && (
        <AccordionLineItem
          icon="icon-card2"
          to="/activate-card"
          state={{ account }}
          onClick={() => {
            trackEvent({
              event: {
                category: "accordion",
                action: "in-page-navigation",
                location: "manage-my-account",
                label: "Activate my card",
              },
            });
          }}
        >
          Activate my card
        </AccordionLineItem>
      )}
      {showTempCardLock && (
        <AccordionLineItem
          component={Link}
          icon="third-party-authority"
          to="/temporarily-lock-card"
          state={{ account, product }}
          onClick={() => {
            trackEvent({
              event: {
                category: "accordion",
                action: "open-accordion",
                location: "manage-my-account",
                label: "Temporarily lock card",
              },
            });
          }}
        >
          Temporarily lock card
        </AccordionLineItem>
      )}
      {showPin && (
        <AccordionLineItem
          icon="icon-set-pin"
          to="/set-pin"
          state={{ account }}
          onClick={() => {
            trackEvent({
              event: {
                category: "accordion",
                action: "in-page-navigation",
                location: "manage-my-account",
                label: "Set or change my PIN",
              },
            });
          }}
        >
          Set or change my PIN
        </AccordionLineItem>
      )}
      {showLostStolenOrDamaged && (
        <AccordionLineItem
          component={Link}
          icon="lost-stolen-damaged-card"
          to="/lost-stolen-damaged"
          state={{ account, product }}
          onClick={() => {
            trackEvent({
              event: {
                category: "accordion",
                action: "open-accordion",
                location: "manage-my-account",
                label: "Report lost, stolen or damaged card",
              },
            });
          }}
        >
          Report lost, stolen or damaged card
        </AccordionLineItem>
      )}
    </div>
  );
};
